<template>
  <div class="page-content caller-dete-events">
    <template v-if="!!callerDateEventData">
      <h3 class="mt-4 mb-1">
        Events for Caller {{ customerName }} ({{ phoneNumber }}) {{ eventStartDate === eventEndDate ? `on ${eventStartDate}` : `from ${eventStartDate} to ${eventEndDate}` }}
      </h3>

      <div>
        <el-switch
          v-model="viewMode"
          active-color="#13ce66"
          inactive-color="#13ce66"
          inactive-text="Table View"
          active-value="card"
          inactive-value="table"
          active-text="Card View"
          class="my-2"
        />
      </div>

      <caller-date-event-table
        v-if="isTableMode"
        :date-events="dateEvents"
      />

      <caller-date-event-cards
        v-else
        :date-events="dateEvents"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, computed, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { orderBy } from 'lodash';
import Moment from 'moment-timezone';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { commonDateTimeFormat, defaultTimeZone } from '@/constants/app';
import { CallerDateEvent } from '@/models/caller';
import { callerService } from '@/services/caller';

const CallerDateEventCards = defineAsyncComponent(() => import('./CallerDateEvents/CallerDateEventCards.vue') as any);

const CallerDateEventTable = defineAsyncComponent(() => import('./CallerDateEvents/CallerDateEventTable.vue') as any);

const route = useRoute();

const { showErrorPopupDialog } = useSwalDialog();

const callerId = (route.query?.callerId as string) || '';
const eventStartDate = (route.query?.eventStartDate as string) || '';
const eventEndDate = (route.query?.eventEndDate as string) || '';
const exclude = route.query?.exclude ? (Array.isArray(route.query.exclude) ? route.query.exclude : [route.query.exclude]) : [];
const includeRides = !exclude.includes('rides');
const includeRideActivities = !exclude.includes('rideActivities');
const includeServiceChecks = !exclude.includes('serviceChecks');
const includeScheduledRides = !exclude.includes('scheduledRides');
const includeScheduledRideHistories = !exclude.includes('scheduledRideHistories');
const includeCallLogs = !exclude.includes('callLogs');
const includeTextMessages = !exclude.includes('textMessages');

const viewMode = ref('table');
const callerDateEventData: Ref<CallerDateEvent | null> = ref(null);

const isTableMode = computed(() => viewMode.value === 'table');

const customerName = computed(() => callerDateEventData.value?.customerName || '');
const phoneNumber = computed(() => callerDateEventData.value?.phoneNumber || '');
const timeZone = computed(() => callerDateEventData.value?.timeZone || defaultTimeZone);

const rides = computed(() => callerDateEventData.value?.rides || []);
const rideActivities = computed(() => callerDateEventData.value?.rideActivities || []);
const scheduledRides = computed(() => callerDateEventData.value?.scheduledRides || []);
const scheduledRideHistories = computed(() => callerDateEventData.value?.scheduledRideHistories || []);
const callLogs = computed(() => callerDateEventData.value?.callLogs || []);
const textMessages = computed(() => callerDateEventData.value?.textMessages || []);
const serviceChecks = computed(() => callerDateEventData.value?.serviceChecks || []);

const dateEvents = computed(() => {
  let data = [];

  for (const ride of rides.value) {
    data.push({
      ...ride,
      timeStampText: util.formatDateTime(ride.createdAt, commonDateTimeFormat, timeZone.value),
      category: 'ride',
    });
  }

  for (const callLog of callLogs.value) {
    data.push({
      ...callLog,
      timeStampText: util.formatDateTime(callLog.createdAt, commonDateTimeFormat, timeZone.value),
      category: 'callLog',
    });
  }

  for (const rideActivity of rideActivities.value) {
    data.push({
      ...rideActivity,
      timeStampText: util.formatDateTime(rideActivity.createdAt, commonDateTimeFormat, timeZone.value),
      category: 'rideActivity',
    });
  }

  for (const scheduledRide of scheduledRides.value) {
    const utcPickupDateTime = `${scheduledRide.pickupDate}T${scheduledRide.pickupTime}:00.000Z`;
    const timeStampText = Moment.utc(utcPickupDateTime).tz(timeZone.value).format(commonDateTimeFormat);

    data.push({
      ...scheduledRide,
      category: 'scheduledRide',
      createdAt: utcPickupDateTime,
      timeStampText,
    });
  }

  for (const scheduledRideHistory of scheduledRideHistories.value) {
    data.push({
      ...scheduledRideHistory,
      timeStampText: util.formatDateTime(scheduledRideHistory.createdAt, commonDateTimeFormat, timeZone.value),
      category: 'scheduledRideHistory',
    });
  }

  for (const textMessage of textMessages.value) {
    data.push({
      ...textMessage,
      timeStampText: util.formatDateTime(textMessage.createdAt, commonDateTimeFormat, timeZone.value),
      category: 'textMessage',
    });
  }

  for (const serviceCheck of serviceChecks.value) {
    data.push({
      ...serviceCheck,
      timeStampText: util.formatDateTime(serviceCheck.createdAt, commonDateTimeFormat, timeZone.value),
      category: 'serviceCheck',
    });
  }

  data = orderBy(data, ['createdAt']);

  return data;
});

const loadData = async () => {
  if (!callerId || !eventStartDate || !eventEndDate) {
    showErrorPopupDialog({
      text: 'Invalid params are provided',
    });
    return;
  }

  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Caller Date Events...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    callerDateEventData.value = await callerService.fetchCallerDateEvents({
      callerId,
      date: eventStartDate,
      endDate: eventEndDate,
      includeRides,
      includeRideActivities,
      includeServiceChecks,
      includeScheduledRides,
      includeScheduledRideHistories,
      includeCallLogs,
      includeTextMessages,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  loading.close();
};

loadData();
</script>

<style lang="scss">
.caller-dete-events {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 1200px;
  margin: 0 auto;
  padding-top: 30px;
  padding-bottom: 30px;

  .custom-collapse {
    width: 100%;

    .event-title {
      display: flex;
      justify-content: space-between;
      padding-right: 30px;
      font-size: 16px;
    }
    .multi-line-text {
      white-space: pre-line;
    }
  }

  .table-view {
    .el-table__cell.smaller {
      font-size: 10px;
    }
  }
}
</style>
